import React from 'react';
import { connect } from 'react-redux';
import { useMutation } from 'react-apollo-hooks';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import { reduxForm } from 'redux-form';
import { validateEmail } from 'utils';
import { SEARCH_BY_FILED } from 'constants';
import { mapProps } from 'shell/hoc/mapProps';
import { withConfig } from 'shell/hoc/withConfig';
import { createFullClientsProfile } from 'modules/clients/actions';
import { createMessageOneToManyInboxAndConversation } from 'graphQL/mutation';
import { sendMessageWithAttributes } from 'shell/external/utils';
import styles from './AddProfileForm.styles';
import AddProfileForm from './AddProfileForm';

// HoC intended to complete the profile creation flow (post with `sendMessageWithAttributes`)
const handleProfileCreationHoC = (Component) => {
  return (props) => {
    const { config, user, history } = props;
    const [postMutation] = useMutation(createMessageOneToManyInboxAndConversation);

    const onProfileCreate = (customer) => {
      const { cmdUri } = customer;
      if (cmdUri) {
        sendMessageWithAttributes({ user, config, customer, templateType: 'clientCreation', postMutation });
        history.push(`/clients/universal-profile/${cmdUri}/about`);
      }
    };

    return <Component onProfileCreate={onProfileCreate} {...props} />;
  };
};

// Logic of initial form fields are implemented according AC-2 of https://jira.mynmg.com/browse/OSP-3029
export const prepareInitialFormValues = ({ searchTerm, searchBy, ...restOwnProps }) => {
  const initialValues = {};
  const trimmedSearchTerm = searchTerm.trim();

  if (trimmedSearchTerm) {
    // eslint-disable-next-line default-case
    switch (searchBy) {
      case SEARCH_BY_FILED.NAME:
        {
          const firstSpacePosition = trimmedSearchTerm.indexOf(' ');

          if (firstSpacePosition > -1) {
            initialValues.firstName = trimmedSearchTerm.slice(0, firstSpacePosition);
            initialValues.lastName = trimmedSearchTerm.slice(firstSpacePosition + 1);
          } else {
            initialValues.firstName = trimmedSearchTerm;
          }
        }
        break;

      case SEARCH_BY_FILED.PHONE:
        initialValues.phone = trimmedSearchTerm;
        break;

      case SEARCH_BY_FILED.EMAIL:
        initialValues.email = trimmedSearchTerm;
        break;
    }
  }

  return { ...restOwnProps, initialValues };
};

// Validate Redux form
export const validateForm = (values = {}) => {
  const requiredFields = ['firstName', 'lastName', 'phone', 'phoneType', 'email'];

  // validate required fields
  const errors = requiredFields.reduce((res, fieldName) => {
    if (!values[fieldName]) {
      res[fieldName] = 'Required';
    }

    return res;
  }, {});

  // specific validations

  if (!errors.phone && values.phone.length !== 10) {
    errors.phone = 'Invalid phone number';
  }

  if (!errors.email && !validateEmail(values.email)) {
    errors.email = 'Invalid email';
  }

  return errors;
};

const onSubmit = async (values, dispatch, { history, onProfileCreate }) => {
  const res = await dispatch(createFullClientsProfile(values, history));

  // Redirect user to appropriate route in case of posted profile is already existing
  if (res.status === 'exists') {
    const { customers } = res.data;

    history.push({
      pathname: '/clients/accounts-exist',
      state: { customers },
    });

    return;
  }

  // Complete profile creation flow with to pass new profile data to `handleProfileCreationHoC` callback
  if (res.status === 'created') {
    onProfileCreate(res.data);
  }
};

const mapStateToProps = ({ user }) => ({
  user: user.data,
});

export default compose(
  withStyles(styles),
  mapProps(prepareInitialFormValues),
  withConfig(),
  connect(mapStateToProps),
  handleProfileCreationHoC,
  reduxForm({
    form: 'addProfileForm',
    onSubmit,
    validate: validateForm,
  }),
)(AddProfileForm);
